/**
 * Turn a session's log stream into timeline entries. The agent streams one of:
 *
 *   - a watched session's timeline, one compact line per event (agents/agent-ts/src/
 *     timeline-format.ts): `[hh:mm:ss] You: ...`, `Claude: ...` (the agent's name: Codex,
 *     Gemini CLI, ...; further lines of the text start with two spaces), `~ reasoning`,
 *     `> Tool: hint`, `! error`, and anything else as a line from Orgus itself;
 *   - a headless run's live log (agents/agent-ts/src/runs.ts renderStreamEvent): plain
 *     text, `> Tool: hint` lines, `  tool error: ...`, `Session <id> started`, `Finished: ...`;
 *   - raw JSONL (Claude Code transcript lines or stream-json events), still accepted.
 *
 * Prompts and replies keep their lines: the console renders them as Markdown.
 * agents/agent-ts/src/timeline-contract.test.ts runs the agent's formatters through this
 * parser so the two cannot drift apart again.
 */
import { SESSION_SAY_TEXT_MAX, clip, clipText } from '@orgus/protocol';

export type TranscriptKind = 'prompt' | 'say' | 'thinking' | 'tool' | 'result' | 'error' | 'system';

export interface TranscriptEntry {
  /** Stable within one parse: the line index and block index. */
  key: string;
  kind: TranscriptKind;
  at: number | null;
  text: string;
  /** Tool name for `tool` entries. */
  tool?: string;
}

type Block = { type?: string; text?: unknown; name?: unknown; input?: unknown; content?: unknown; is_error?: unknown };

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const baseName = (p: string) => p.replace(/\\/g, '/').split('/').pop() ?? p;

/** What a tool call was for, in a few words. */
export function describeTool(name: string, input: unknown): string {
  const i = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  switch (name) {
    case 'Bash':
      return str(i.description) || clip(str(i.command), 90);
    case 'Edit':
    case 'Write':
    case 'Read':
    case 'NotebookEdit':
      return baseName(str(i.file_path ?? i.notebook_path));
    case 'Agent':
    case 'Task':
      return str(i.description) || clip(str(i.prompt), 90);
    case 'Workflow':
      return str(i.name) || 'Workflow script';
    case 'Grep':
    case 'Glob':
      return clip(str(i.pattern), 70);
    case 'SendMessage':
      return i.to ? `To ${str(i.to)}` : '';
    case 'WebFetch':
    case 'WebSearch':
      return clip(str(i.url ?? i.query), 90);
    default:
      return '';
  }
}

/** System reminders and command wrappers start with a tag; they are not what the person typed. */
const isMarkup = (t: string) => t.trimStart().startsWith('<');

function textOf(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return (content as Block[])
      .filter((b) => b?.type === 'text' && typeof b.text === 'string')
      .map((b) => b.text as string)
      .join(' ');
  }
  return '';
}

const STAMPED = /^\[(\d\d):(\d\d):(\d\d)\] (.*)$|^\[--:--:--\] (.*)$/;
/** A headless run's tool error line (runs.ts renderStreamEvent). */
const TOOL_ERROR = /^\s+tool error: /;
const DAY_MS = 24 * 60 * 60_000;

/** A bold subject glued to the sentence before it (`next.**Subject**`), as older ACP logs carry it. */
const GLUED_SUBJECT = /([.!?])(\*\*[^*\s][^*\n]{0,118}[^*\s]\*\*)/g;
/** A step's bold subject at the start of a paragraph with its body on the same line. */
const SUBJECT_THEN_BODY = /(^|\n\n)(\*\*[^*\s][^*\n]{0,118}[^*\s]\*\*)[ \t]+(?=\S)/g;

/**
 * Reasoning steps (and an ACP agent's reply) written as one run of `**Subject** body` pieces,
 * each subject starting its own paragraph; in reasoning, the subject also sits above its body.
 */
export function tidySteps(text: string, thinking = false): string {
  const t = text.replace(GLUED_SUBJECT, '$1\n\n$2');
  return thinking ? t.replace(SUBJECT_THEN_BODY, '$1$2\n\n') : t;
}

/** A `> Tool: hint` body: the tool name, then what it was for. */
function toolLine(body: string): { tool: string; text: string } {
  const i = body.indexOf(': ');
  return i > 0 ? { tool: body.slice(0, i).trim(), text: body.slice(i + 2).trim() } : { tool: body.trim() || 'Tool', text: '' };
}

/** The names an agent speaks under when the caller does not say (Claude Code, Codex). */
const SPEAKERS: readonly string[] = ['Claude', 'Codex'];

/**
 * A result line's tail (", 96 turns, 1022 s") as the owner reads it: "Turn ended (96 turns,
 * 17 min)", or "Turn ended with an error (1 turn, 1 s)". Exported for tests.
 */
export function turnEndedText(problem: string | null, tail: string): string {
  const facts = tail
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => {
      const turns = /^(\d+) turns?$/.exec(x);
      if (turns) return `${turns[1]} ${turns[1] === '1' ? 'turn' : 'turns'}`;
      const secs = /^(\d+) s$/.exec(x);
      if (!secs) return x;
      const n = Number(secs[1]);
      return n < 60 ? `${n} s` : n < 3600 ? `${Math.round(n / 60)} min` : `${Math.floor(n / 3600)} h ${String(Math.round((n % 3600) / 60)).padStart(2, '0')} min`;
    });
  const head = problem ? `Turn ended with ${problem === 'error' ? 'an error' : problem}` : 'Turn ended';
  return facts.length ? `${head} (${facts.join(', ')})` : head;
}

/** One line of the compact text formats, or null for a line that is not one. */
function textLine(t: string, speakers: readonly string[]): { kind: TranscriptKind; text: string; tool?: string; secs: number | null; plain?: boolean; tidy?: boolean } | null {
  const m = STAMPED.exec(t);
  if (m) {
    const secs = m[1] != null ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) : null;
    const rest = (m[4] ?? m[5] ?? '').trimEnd();
    if (rest.startsWith('You: ')) return { kind: 'prompt', text: rest.slice(5), secs };
    // The agent's name speaks: Claude for Claude Code, else its label (timeline-format.ts).
    const say = speakers.find((s) => rest.startsWith(`${s}: `));
    // Claude Code's words are shown exactly; another agent's run-on steps get their breaks back.
    if (say) return { kind: 'say', text: rest.slice(say.length + 2), secs, ...(say === 'Claude' ? {} : { tidy: true }) };
    if (rest.startsWith('~ ')) return { kind: 'thinking', text: rest.slice(2), secs, tidy: true };
    if (rest.startsWith('> ')) return { kind: 'tool', ...toolLine(rest.slice(2)), secs };
    if (rest.startsWith('! ')) return { kind: 'error', text: rest.slice(2), secs };
    return { kind: 'system', text: rest, secs };
  }
  // A headless run's live log.
  if (t.startsWith('> ')) return { kind: 'tool', ...toolLine(t.slice(2)), secs: null };
  if (TOOL_ERROR.test(t)) return { kind: 'error', text: t.replace(TOOL_ERROR, ''), secs: null };
  // A run's result line marks a turn's end, not the process's (ORGU-132): the exit says Ended.
  const fin = /^Finished(?:: ([\w-]+)| with an error)(.*)$/.exec(t);
  if (fin) {
    const ok = fin[1] === 'success' || fin[1] === 'done';
    return { kind: ok ? 'result' : 'error', text: turnEndedText(ok ? null : (fin[1] ?? 'error').replace(/_/g, ' '), fin[2] ?? ''), secs: null };
  }
  const init = /^Session \S+ started(?: \(model (.+)\))?$/.exec(t);
  if (init) return { kind: 'system', text: init[1] ? `Started with ${init[1]}` : 'Started', secs: null };
  if (/^Starting Claude Code in /.test(t)) return { kind: 'system', text: t, secs: null };
  const exit = /^Claude Code exited with code (\S+)\.$/.exec(t);
  if (exit) return { kind: exit[1] === '0' ? 'system' : 'error', text: exit[1] === '0' ? 'Ended: Claude Code exited.' : `Ended: ${t}`, secs: null };
  if (/^Claude Code could not be started/.test(t)) return { kind: 'error', text: t, secs: null };
  return { kind: 'say', text: t, secs: null, plain: true };
}

/**
 * Times of day become timestamps: the newest is today (yesterday if that would be in the
 * future), and walking back, each time later than the one after it crossed a midnight.
 */
function stampDays(secs: (number | null)[], now: number): (number | null)[] {
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const out: (number | null)[] = new Array(secs.length).fill(null);
  let day = midnight.getTime();
  let next: number | null = null;
  for (let i = secs.length - 1; i >= 0; i--) {
    const s = secs[i];
    if (s == null) continue;
    if (next === null) {
      if (day + s * 1000 > now + 60_000) day -= DAY_MS;
    } else if (s > next) day -= DAY_MS;
    out[i] = day + s * 1000;
    next = s;
  }
  return out;
}

/**
 * Parse every complete line. A trailing partial line is ignored until the rest arrives.
 * `speaker` is the name the session's agent speaks under (speakerOf), beside Claude and Codex.
 */
export function parseTranscript(raw: string, max = 400, now: number = Date.now(), speaker?: string): TranscriptEntry[] {
  const speakers = speaker && !SPEAKERS.includes(speaker) ? [...SPEAKERS, speaker] : SPEAKERS;
  const out: TranscriptEntry[] = [];
  const lines = raw.split('\n');
  if (!raw.endsWith('\n')) lines.pop();
  // Text-format entries and their time of day, stamped with a date once every line is read.
  const texts: { entry: TranscriptEntry; secs: number | null }[] = [];
  // Reasoning and an ACP agent's replies, given their step breaks once their lines are joined.
  const tidy: TranscriptEntry[] = [];
  let lastPlain: TranscriptEntry | null = null;
  // The newest `You: ...`, `Claude: ...` or `~ ...` entry of the timeline format: lines starting with two spaces continue it.
  let lastSay: TranscriptEntry | null = null;
  // A blank line inside a headless reply: its next line starts a new paragraph.
  let gap = false;
  lines.forEach((line, n) => {
    const t = line.replace(/\r$/, '');
    // A headless run's `  tool error: ...` line may follow its echo of your message: never part of it.
    if (lastSay && out[out.length - 1] === lastSay && t.startsWith('  ') && !TOOL_ERROR.test(t)) {
      if (lastSay.text.length < SESSION_SAY_TEXT_MAX) lastSay.text = `${lastSay.text}\n${t.slice(2)}`.slice(0, SESSION_SAY_TEXT_MAX);
      return;
    }
    if (!t.trim()) {
      gap = true;
      return;
    }
    const paragraph = gap;
    gap = false;
    if (t.trim()[0] !== '{') {
      const p = textLine(t, speakers);
      if (!p) return;
      if (p.plain && lastPlain && out[out.length - 1] === lastPlain) {
        // Multi-line assistant text in a headless log: one entry.
        lastPlain.text = `${lastPlain.text}${paragraph ? '\n\n' : '\n'}${p.text}`.slice(0, SESSION_SAY_TEXT_MAX);
        return;
      }
      const entry: TranscriptEntry = { key: `${n}.0`, kind: p.kind, at: null, text: p.kind === 'tool' ? p.text : p.text.slice(0, SESSION_SAY_TEXT_MAX), ...(p.tool ? { tool: p.tool } : {}) };
      out.push(entry);
      texts.push({ entry, secs: p.secs });
      if (p.tidy) tidy.push(entry);
      lastPlain = p.plain ? entry : null;
      lastSay = (p.kind === 'say' || p.kind === 'prompt' || p.kind === 'thinking') && !p.plain ? entry : null;
      return;
    }
    lastPlain = null;
    lastSay = null;
    const tj = t.trim();
    let j: Record<string, unknown>;
    try {
      j = JSON.parse(tj) as Record<string, unknown>;
    } catch {
      return;
    }
    if (j.isSidechain || j.isMeta) return;
    const at = typeof j.timestamp === 'string' ? Date.parse(j.timestamp) || null : null;
    const message = (j.message ?? {}) as { content?: unknown };
    const content = message.content;
    const push = (e: Omit<TranscriptEntry, 'key' | 'at'>, b = 0) => out.push({ key: `${n}.${b}`, at, ...e });

    if (j.type === 'user') {
      if (Array.isArray(content)) {
        (content as Block[]).forEach((b, i) => {
          if (b?.type === 'tool_result' && b.is_error === true) {
            const text = clip(textOf(b.content) || 'The tool reported an error.', 200);
            push({ kind: 'error', text }, i);
          }
        });
      }
      const text = textOf(content);
      if (text && !isMarkup(text)) push({ kind: 'prompt', text: clipText(text, SESSION_SAY_TEXT_MAX) }, 99);
    } else if (j.type === 'assistant' && Array.isArray(content)) {
      (content as Block[]).forEach((b, i) => {
        if (b?.type === 'text' && typeof b.text === 'string' && b.text.trim()) push({ kind: 'say', text: clipText(b.text, SESSION_SAY_TEXT_MAX) }, i);
        else if (b?.type === 'tool_use') {
          const name = str(b.name) || 'Tool';
          push({ kind: 'tool', tool: name, text: describeTool(name, b.input) }, i);
        }
      });
    } else if (j.type === 'result') {
      const ok = j.is_error !== true && j.subtype !== 'error';
      const text = ok ? clip(str(j.result) || 'Finished.', 300) : clip(str(j.result) || 'The run ended with an error.', 300);
      push({ kind: ok ? 'result' : 'error', text });
    } else if (j.type === 'system' && j.subtype === 'init') {
      push({ kind: 'system', text: j.model ? `Started with ${str(j.model)}` : 'Started' });
    }
  });
  for (const e of tidy) e.text = tidySteps(e.text, e.kind === 'thinking').slice(0, SESSION_SAY_TEXT_MAX);
  if (texts.length) {
    const stamps = stampDays(
      texts.map((x) => x.secs),
      now,
    );
    texts.forEach((x, i) => {
      x.entry.at = stamps[i] ?? null;
    });
  }
  return out.length > max ? out.slice(out.length - max) : out;
}
